import React, { useState, useEffect } from 'react';
import {
  Stack, Typography, Box, Icon,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useForm, useWatch } from 'react-hook-form';
import { useFetch } from 'use-http';
import BaseModal from '../../../../common/atoms/BaseModal/BaseModal';
import { usePhotoGallery } from '../../../../hooks/usePhotoGallery';
import LoadingSpinner from '../../../../components/generic/LoadingSpinner';
import { useAuthContext } from '../../../../providers/auth-provider';
import StyledTextField from '../../../../common/atoms/inputs/StyledTextField';
import StyledTextArea from '../../../../common/atoms/inputs/StyledTextarea';
import StyledDropdown from '../../../../common/atoms/inputs/StyledDropdown';
import BackrsButton from '../../../../common/atoms/backrsButton/BackrsButton';
import { useAppState } from '../../../../providers/app-state/app-state-provider';
import appStrings from '../../../../common/app_strings';
import appColors from '../../../../common/app_colors';
import { handleError, handleMediaError } from './utils';

const { wholeMeProfileStory: { modal, errors } } = appStrings;
const { buttons, text } = appColors;

const WholeMeUploadManagerModal = ({
  type,
  isOpen,
  handleClose,
  handleDeleteClick,
  onDocumentPost,
  questionId,
  selectedItem,
  mode,
  categoryId,
  optionId,
  defaultTitle,
  onStoryChange,
}) => {
  const { user } = useAuthContext();
  const {
    loading, takePdf, takeVideo, takeWebVideo,
  } = usePhotoGallery();
  const {
    control, handleSubmit, reset, setValue,
  } = useForm();
  const { platform: { isNativePlatform } } = useAppState();
  const { post, put, response } = useFetch();
  const [media, setMedia] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const watchTitle = useWatch({ control, name: 'title' });
  const watchDescription = useWatch({ control, name: 'description' });

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const isMediaDocument = type === 'document';

  const isStudent = ['Student', 'Scholar', 'Foundations'].some((role) => user?.roles?.some((userRole) => userRole?.name === role));

  const resetMedia = () => {
    setMedia(null);
  };

  const handleModalClose = () => {
    reset({
      title: '',
      description: '',
      visibility: 'public',
    });
    setMedia(null);
    handleClose();
  };

  const getOptionId = (data) => {
    switch (data?.file_type) {
      case modal.resume.toLocaleLowerCase():
        return modal.resume.toLocaleLowerCase();
      case modal.transcript.toLocaleLowerCase():
        return modal.transcript.toLocaleLowerCase();
      default:
        return null;
    }
  };

  const saveItemToProfile = async (data, isDocument = false) => {
    const url = mode === 'edit'
      ? `/users-profile-answers/${selectedItem?.id}`
      : '/users-profile-answers/';

    const payload = {
      category_id: categoryId || 'story',
      question_id: isDocument ? 'document' : questionId,
      option_id: optionId || getOptionId(data),
      title: data?.title,
      description: data?.description,
      media_id: isDocument ? media?.id : selectedItem?.media_id,
      visibility: data?.visibility,
      user_id: user?.id,
      media_type: type,
    };

    try {
      setIsLoading(true);
      const res = mode === 'edit' ? await put(url, payload) : await post(url, payload);

      if (!response.ok) {
        console.error(`${modal.failedToSave}`, response);
        throw new Error(modal.failedToSave);
      }

      reset();
      if (isDocument) {
        onDocumentPost(media);
      } else {
        console.log('not document');
        onStoryChange();
      }
      return res;
    } catch (e) {
      console.error(modal.failedToSave, e.message);
      Sentry.captureException(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemSubmit = async (data, isDocument = false) => {
    try {
      await saveItemToProfile(data, isDocument);
    } catch (e) {
      Sentry.captureException(e);
      handleError(e);
    } finally {
      resetMedia();
      handleModalClose();
    }
  };

  const handleMediaUpload = async (uploader) => {
    if (loading) return;
    setIsLoading(true);
    try {
      const { webviewPath, media: mediaData } = await uploader();
      if (mediaData) {
        setMedia({ webviewPath, id: mediaData.id, type: mediaData.type });
      }
    } catch (e) {
      handleModalClose();
      handleMediaError(e, errors.document);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFileType = () => {
    if (isStudent) {
      return (
        <StyledDropdown
          control={control}
          name="file_type"
          required
          placeholder="File Type"
          choices={[
            { value: 'resume', label: modal.resume },
            { value: 'transcript', label: modal.transcript },
            { value: 'other', label: modal.other },
          ]}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    const isDisabled = mode === 'edit'
      ? !watchTitle
      : (mode !== 'edit' && !media?.id) || !watchTitle || !watchDescription;

    setIsSubmitDisabled(isDisabled);
  }, [media, watchTitle, watchDescription]);

  useEffect(() => {
    const {
      title, description, visibility, media: mediaMeta,
    } = selectedItem || {};

    if (selectedItem) {
      setValue('title', title || '');
      setValue('description', description || '');
      setValue('visibility', visibility || 'public');

      if (mediaMeta) {
        setMedia(mediaMeta);
      }
    } else {
      handleModalClose();
    }
  }, [selectedItem, setValue, reset]);

  const renderForm = (titlePlaceholder, handleSubmitFunc, showUploadButton = true) => (
    isLoading ? <LoadingSpinner /> : (
      <Stack
        sx={{
          height: '100%',
          mt: '10px',
        }}
      >
        <Typography
          variant="h7"
          sx={{
            textAlign: 'center',
            fontWeight: 500,
            mt: '-10px',
            pb: '20px',
          }}
        >
          {isMediaDocument ? modal.uploadDocument : modal.editStoryTitle}
        </Typography>
        <form onSubmit={handleSubmit(handleSubmitFunc)}>
          <Stack spacing={2}>
            <StyledTextField
              control={control}
              defaultValue=""
              name="title"
              placeholder={defaultTitle || titlePlaceholder}
              required
              type="text"
            />
            <Stack spacing={0}>
              <StyledTextArea
                control={control}
                defaultValue=""
                name="description"
                placeholder={modal.description}
                required
                maxCharacters={selectedItem?.media_type === 'video' ? 50 : null}
              />
              {selectedItem?.media_type === 'video' && (
                <Typography sx={{
                  fontSize: '13px',
                  overflow: 'hidden',
                  color: text.grey,
                  textAlign: 'right',
                }}
                >
                  {modal.characterLimit(watchDescription?.length || 0)}
                </Typography>
              )}
            </Stack>
            {renderFileType()}
            <StyledDropdown
              control={control}
              name="visibility"
              required
              placeholder={modal.whoCanSee}
              defaultValue={selectedItem?.visibility || 'public'}
              choices={[
                { value: 'panel', label: modal.panel },
                { value: 'public', label: modal.public },
              ]}
            />
            {media?.webviewPath?.name && (
              <Typography sx={{
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: text.darkNavy,
                fontWeight: '700',
                textWrap: 'nowrap',
                width: '100%',
              }}
              >
                <Icon
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    transform: 'translateY(6px)',
                  }}
                >
                  <img
                    src="/assets/mingcute_file-line.svg"
                    alt="media icon"
                  />
                </Icon>
                {media?.webviewPath?.name}
              </Typography>
            )}
            {showUploadButton && (
              <BackrsButton
                label={isMediaDocument ? modal.uploadFile : modal.uploadVideo}
                onClick={isMediaDocument ? () => handleMediaUpload(takePdf)
                  : () => handleMediaUpload(isNativePlatform ? takeVideo : takeWebVideo)}
                imgSrc="/assets/profile/story/upload-arrow.svg"
                isIconLeftAligned
                disableRipple
                buttonId="profile-document-file-button"
                buttonStylesOverride={{
                  width: '131px',
                  height: '44px',
                  mb: '10px',
                  backgroundColor: buttons.uploadMediaBackground,
                  color: text.uploadMediaText,
                  borderRadius: '15px',
                  marginLeft: '-10px',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: buttons.uploadMediaBackground,
                  },
                }}
              />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <BackrsButton
                buttonId="profile-document-submit-button"
                label="Save To Profile"
                isLoading={loading}
                onClick={handleSubmit(handleSubmitFunc)}
                buttonStylesOverride={{
                  width: '190px',
                  height: '40px',
                  backgroundColor: buttons.profileSaveButton,
                  color: text.whiteText,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: buttons.profileSaveButton,
                  },
                }}
                isDisabled={isSubmitDisabled}
              />
              {mode === 'edit' && (
                <Typography
                  onClick={() => (handleDeleteClick(selectedItem))}
                  sx={{
                    maxWidth: '120px',
                    color: text.darkNavy,
                    fontSize: '14px',
                    cursor: 'pointer',
                    pt: '10px',
                  }}
                >
                  {modal.deleteThis}
                </Typography>
              )}
            </Box>
          </Stack>
        </form>
      </Stack>
    )
  );

  const renderContent = () => {
    switch (type) {
      case 'document':
        return renderForm(modal.fileName, (data) => handleItemSubmit(data, true));
      case 'video':
      case 'videos':
        return renderForm(modal.videoTitle, (data) => handleItemSubmit(data, false), false);
      default:
        return renderForm(modal.textTitle, (data) => handleItemSubmit(data, false), false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      handleClose={handleModalClose}
      modalOverrideStyles={{
        width: '400px',
        maxWidth: '400px',
        minHeight: '400px',
        padding: '10px',
        backgroundColor: '#FFF',
        borderRadius: '8px',
      }}
    >
      {renderContent()}
    </BaseModal>
  );
};

export default WholeMeUploadManagerModal;
